/**
 * Save Manager
 * Handles game save/load and autosave
 */

import { GameEvent } from '@/types';
import { BALANCE_CONFIG } from '@/data';
import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { equipmentManager } from '../equipment/EquipmentManager';
import { CURRENT_SAVE_VERSION, runMigrations } from './migrations';

const AUTOSAVE_INTERVAL = BALANCE_CONFIG.AUTOSAVE_INTERVAL * 1000; // Convert to ms
const STORAGE_KEY = 'fishing-empire-save';

export class SaveManager {
  private autosaveTimer: number | null = null;

  /**
   * Initialize autosave
   */
  startAutosave(): void {
    if (this.autosaveTimer) {
      this.stopAutosave();
    }

    this.autosaveTimer = window.setInterval(() => {
      this.save();
    }, AUTOSAVE_INTERVAL);
  }

  /**
   * Stop autosave
   */
  stopAutosave(): void {
    if (this.autosaveTimer) {
      window.clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
    }
  }

  /**
   * Manual save to localStorage
   */
  save(): boolean {
    try {
      const state = useGameStore.getState();
      const saveData = {
        playerState: state.playerState,
        version: CURRENT_SAVE_VERSION,
        savedAt: Date.now(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(saveData));

      eventBus.emit(GameEvent.GAME_SAVED, { timestamp: Date.now() });

      return true;
    } catch (error) {
      console.error('\uD83D\uDCBE Save failed:', error);
      return false;
    }
  }

  /**
   * Load game from localStorage
   * This is called on app init
   */
  load(): void {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (!savedData) {
        console.log('\uD83D\uDCBE No saved game found, starting fresh');
        return;
      }

      const parsed = JSON.parse(savedData);

      // Check if we have valid playerState data
      if (!parsed.playerState || !parsed.playerState.inventory) {
        console.warn('\u26A0\uFE0F Invalid save format, starting fresh');
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Run versioned migrations
      runMigrations(parsed);

      // Reset all haulers to idle state on load (prevent mid-trip corruption)
      // This runs on every load, not just during migration
      if (parsed.playerState.haulerBuddies && Array.isArray(parsed.playerState.haulerBuddies)) {
        parsed.playerState.haulerBuddies.forEach((hauler: any) => {
          if (hauler.state && hauler.state !== 'idle') {
            console.log(`\uD83D\uDCBE Resetting hauler ${hauler.slotNumber} from ${hauler.state} to idle`);
            hauler.state = 'idle';
            hauler.currentCargo = [];
            hauler.currentCargoCount = 0;
            hauler.stateStartedAt = null;
            hauler.stateCompletesAt = null;
          }
        });
      }

      const state = useGameStore.getState();
      state.setPlayerState(parsed.playerState);

      equipmentManager.recalculateStats();

      eventBus.emit(GameEvent.GAME_LOADED, { timestamp: Date.now() });

      console.log('\uD83D\uDCBE Game loaded:', {
        version: parsed.version,
        inventory: `${parsed.playerState.inventory.currentCount}/${parsed.playerState.inventory.capacity}`,
        currency: parsed.playerState.currency,
      });
    } catch (error) {
      console.error('\uD83D\uDCBE Load failed:', error);
      console.warn('\u26A0\uFE0F Clearing corrupted save');
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Reset game to default state
   */
  resetGame(): void {
    const state = useGameStore.getState();
    state.resetGame();
    equipmentManager.recalculateStats();
  }

  /**
   * Export save as JSON (for backup)
   */
  exportSave(): string {
    const state = useGameStore.getState();
    return JSON.stringify(state.playerState, null, 2);
  }

  /**
   * Import save from JSON (future feature)
   */
  importSave(json: string): boolean {
    try {
      const parsed = JSON.parse(json);
      const state = useGameStore.getState();
      state.setPlayerState(parsed);
      equipmentManager.recalculateStats();
      this.save();
      return true;
    } catch (error) {
      console.error('Import failed:', error);
      return false;
    }
  }
}

// Singleton instance
export const saveManager = new SaveManager();
